import { useMemo, useCallback, useState, useEffect } from "react";
import { useTrips } from "./useTrips";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "./useAuth";
import { useAnalytics, ANALYTICS_EVENTS } from "./useAnalytics";
import { useI18n } from "@/lib/i18n";
import { clearHandoff } from "@/lib/navigatorSession";
import { useOnlineStatus } from "./useOnlineStatus";
import {
  buildSnapshotFromTrip,
  clearActiveTripSnapshot,
  readActiveTripSnapshot,
  snapshotToTripLike,
  writeActiveTripSnapshot,
  type ActiveTripSnapshot,
} from "@/lib/activeTripSnapshot";
import { clearQueueForTrip, enqueueOp } from "@/lib/activeTripQueue";

export const useActiveTrip = () => {
  const { user } = useAuth();
  const { trips, isLoading } = useTrips();
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const { t } = useI18n();
  const online = useOnlineStatus();
  const [conflictDialog, setConflictDialog] = useState<{
    existingTrip: any;
    newTripId: string;
    action: "start" | "resume" | "reactivate";
  } | null>(null);
  // 11a — last successfully captured snapshot of the active trip. Read on
  // mount per-user; refreshed every time live data lands (see effect below).
  const [snapshot, setSnapshot] = useState<ActiveTripSnapshot | null>(() =>
    readActiveTripSnapshot(null), // user not known yet on first render
  );

  // Find the trip with travel_status = 'active' or 'paused'
  const liveActiveTrip = useMemo(() => {
    return trips.find((t: any) => t.travel_status === "active" || t.travel_status === "paused") || null;
  }, [trips]);

  // 11a — re-hydrate the snapshot from disk once we know who the user is so
  // a hard reload while offline can still surface "where am I in the trip".
  useEffect(() => {
    if (!user?.id) return;
    const fromDisk = readActiveTripSnapshot(user.id);
    if (fromDisk) setSnapshot(fromDisk);
  }, [user?.id]);

  // 11a — every time fresh live data arrives for an active trip, persist a
  // new snapshot. This covers active_stop_index / trip_stops / travel_status
  // changes implicitly (any mutation invalidates the trips query, which
  // re-runs this effect). Snapshot writes are best-effort.
  useEffect(() => {
    if (!user?.id || !liveActiveTrip) return;
    const next = buildSnapshotFromTrip(user.id, liveActiveTrip);
    if (!next) return;
    writeActiveTripSnapshot(next);
    setSnapshot(next);
  }, [user?.id, liveActiveTrip]);

  // 11a — final activeTrip exposed to the app. Prefer live data; fall back
  // to the snapshot only when live data is unavailable AND we know it's a
  // network condition (offline OR isLoading-with-no-result is impossible
  // to disambiguate, so we limit fallback to "offline" to avoid surfacing
  // a stale trip after the user legitimately ended one while online).
  const activeTrip = useMemo(() => {
    if (liveActiveTrip) return liveActiveTrip;
    if (!online && snapshot && snapshot.userId === user?.id) {
      return snapshotToTripLike(snapshot);
    }
    return null;
  }, [liveActiveTrip, online, snapshot, user?.id]);

  const isPaused = activeTrip?.travel_status === "paused";

  // Helper: deactivate existing active/paused trip
  const deactivateExisting = useCallback(async (tripId: string) => {
    const today = new Date().toISOString();
    await supabase
      .from("trips")
      .update({
        travel_status: "completed",
        completed_at: today,
      } as any)
      .eq("id", tripId);
  }, []);

  // Check for conflict and handle
  const checkAndStart = useCallback(async (
    tripId: string,
    action: "start" | "resume" | "reactivate"
  ): Promise<boolean> => {
    // Check if there's already an active/paused trip that isn't this one
    const existing = trips.find(
      (t: any) => (t.travel_status === "active" || t.travel_status === "paused") && t.id !== tripId
    );

    if (existing) {
      setConflictDialog({ existingTrip: existing, newTripId: tripId, action });
      return false; // blocked — user must choose
    }
    return true; // no conflict
  }, [trips]);

  const startTrip = useCallback(async (tripId: string) => {
    const canProceed = await checkAndStart(tripId, "start");
    if (!canProceed) return;

    // DON'T overwrite start_date/end_date — use started_at instead
    const { error } = await supabase
      .from("trips")
      .update({
        travel_status: "active",
        started_at: new Date().toISOString(),
      } as any)
      .eq("id", tripId);

    if (error) {
      if (error.message?.includes("already has an active")) {
        toast({ title: t("myTrips.alreadyActiveTrip"), description: t("myTrips.endActiveFirst"), variant: "destructive" });
      } else {
        toast({ title: t("common.error"), description: error.message, variant: "destructive" });
      }
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["trips"] });
    track(ANALYTICS_EVENTS.TRIP_STARTED, { trip_id: tripId });
    toast({ title: t("myTrips.tripStarted"), description: t("myTrips.tripStartedDesc") });
  }, [queryClient, checkAndStart, track, t]);

  const pauseTrip = useCallback(async (tripId: string) => {
    // 11b: offline → queue, latest status wins (dedupe collapses toggles).
    if (!online && user?.id) {
      enqueueOp(user.id, { type: "status_change", tripId, nextStatus: "paused" });
      toast({
        title: "Åtgärden sparades offline",
        description: "Resan pausas när nätet är tillbaka.",
      });
      return;
    }
    const { error } = await supabase
      .from("trips")
      .update({ travel_status: "paused" } as any)
      .eq("id", tripId);

    if (!error) {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      toast({ title: t("myTrips.tripPaused"), description: t("myTrips.tripPausedDesc") });
    }
  }, [queryClient, t, online, user?.id]);

  const resumeTrip = useCallback(async (tripId: string) => {
    const canProceed = await checkAndStart(tripId, "resume");
    if (!canProceed) return;
    // 11b: offline → queue. Conflict checks run server-side on replay.
    if (!online && user?.id) {
      enqueueOp(user.id, { type: "status_change", tripId, nextStatus: "active" });
      toast({
        title: "Åtgärden sparades offline",
        description: "Resan återupptas när nätet är tillbaka.",
      });
      return;
    }

    const { error } = await supabase
      .from("trips")
      .update({ travel_status: "active" } as any)
      .eq("id", tripId);

    if (error) {
      if (error.message?.includes("already has an active")) {
        toast({ title: t("myTrips.alreadyActiveTrip"), description: t("myTrips.endActiveFirst"), variant: "destructive" });
      }
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["trips"] });
    toast({ title: t("myTrips.tripResumed"), description: t("myTrips.tripResumedDesc") });
  }, [queryClient, checkAndStart, t, online, user?.id]);

  const endTrip = useCallback(async (tripId: string): Promise<{ tripId: string; tripTitle: string } | null> => {
    // Find trip title before ending
    const trip = trips.find((t: any) => t.id === tripId);
    const tripTitle = trip?.title || "Resa";

    // 11b: offline → queue. Snapshot stays so the user keeps seeing the
    // trip until sync confirms it's actually completed server-side.
    if (!online && user?.id) {
      enqueueOp(user.id, { type: "end_trip", tripId });
      toast({
        title: "Åtgärden sparades offline",
        description: "Resan avslutas när nätet är tillbaka.",
      });
      return null;
    }

    const { error } = await supabase
      .from("trips")
      .update({
        travel_status: "completed",
        completed_at: new Date().toISOString(),
      } as any)
      .eq("id", tripId);

    if (!error) {
      // 10a: end-of-trip cleanup. Drop any pending navigator-return banner so
      // a focus event after "Avsluta" can't surface "Tillbaka från Google
      // Maps" on a completed trip.
      clearHandoff();
      // 11a: also drop the local snapshot — the trip is no longer active,
      // so we shouldn't keep showing it as a fallback after going offline.
      clearActiveTripSnapshot();
      setSnapshot(null);
      // 11b: any leftover queued ops for this trip are moot — drop them
      // so the sync engine doesn't keep retrying arrival/skip on a
      // completed resa.
      if (user?.id) clearQueueForTrip(user.id, tripId);
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      track(ANALYTICS_EVENTS.TRIP_COMPLETED, { trip_id: tripId });
      toast({ title: t("myTrips.tripEnded"), description: t("myTrips.tripEndedDesc") });
      return { tripId, tripTitle };
    }
    return null;
  }, [queryClient, trips, t, online, user?.id]);

  const reactivateTrip = useCallback(async (tripId: string) => {
    const canProceed = await checkAndStart(tripId, "reactivate");
    if (!canProceed) return;

    const { error } = await supabase
      .from("trips")
      .update({
        travel_status: "active",
        started_at: new Date().toISOString(),
        completed_at: null,
      } as any)
      .eq("id", tripId);

    if (error) {
      if (error.message?.includes("already has an active")) {
        toast({ title: t("myTrips.alreadyActiveTrip"), description: t("myTrips.endActiveFirst"), variant: "destructive" });
      }
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["trips"] });
    toast({ title: t("myTrips.tripReactivated") });
  }, [queryClient, checkAndStart, t]);

  const revertToPlanned = useCallback(async (tripId: string) => {
    const { error } = await supabase
      .from("trips")
      .update({
        travel_status: "planned",
        started_at: null,
        completed_at: null,
      } as any)
      .eq("id", tripId);

    if (!error) {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      toast({ title: t("myTrips.revertedToPlanned"), description: t("myTrips.revertedToPlannedDesc") });
    }
  }, [queryClient, t]);

  // Resolve conflict: end existing trip and start new one
  const resolveConflict = useCallback(async (choice: "switch" | "keep") => {
    if (!conflictDialog) return;

    if (choice === "switch") {
      await deactivateExisting(conflictDialog.existingTrip.id);
      queryClient.invalidateQueries({ queryKey: ["trips"] });

      // Small delay then perform the original action
      setTimeout(async () => {
        const { action, newTripId } = conflictDialog;
        if (action === "start") {
          await supabase.from("trips").update({
            travel_status: "active",
            started_at: new Date().toISOString(),
          } as any).eq("id", newTripId);
        } else if (action === "resume" || action === "reactivate") {
          await supabase.from("trips").update({
            travel_status: "active",
            ...(action === "reactivate" ? { started_at: new Date().toISOString(), completed_at: null } : {}),
          } as any).eq("id", newTripId);
        }
        queryClient.invalidateQueries({ queryKey: ["trips"] });
        toast({ title: t("myTrips.tripStarted") });
      }, 300);
    }
    // If "keep", just close dialog — user keeps existing trip
    setConflictDialog(null);
  }, [conflictDialog, deactivateExisting, queryClient]);

  return {
    activeTrip,
    isPaused,
    isLoading,
    startTrip,
    pauseTrip,
    resumeTrip,
    endTrip,
    reactivateTrip,
    revertToPlanned,
    // Conflict resolution
    conflictDialog,
    resolveConflict,
    dismissConflict: () => setConflictDialog(null),
    // 11a — offline fallback signals. `isFromSnapshot` is the canonical
    // flag the UI should use to switch into read-only / offline mode.
    isFromSnapshot: !!activeTrip?._fromSnapshot,
    snapshotSavedAt: snapshot?.savedAt ?? null,
    online,
  };
};
